/**
 * ¿HAY QUE PEDIRLE A ESTA PERSONA QUE INSTALE LA APP?
 *
 * Javier lo pidió duro: en el teléfono, la app no se usa sin instalar. Esa
 * decisión hace que ESTE archivo sea el más delicado de la pantalla, porque
 * una respuesta de más deja a alguien AFUERA de la aplicación entera. No es
 * un banner que estorba: es una puerta cerrada.
 *
 * Por eso todo lo que decide vive acá, en funciones puras que reciben lo que
 * leerían del navegador en vez de leerlo solas. Un `navigator.userAgent`
 * consultado adentro de un componente no se puede probar contra los veinte
 * navegadores que existen; una función que recibe el texto, sí.
 *
 * LAS TRES FORMAS DE QUEDAR ADENTRO SIN INSTALAR NADA, y las tres importan:
 *
 *  · la app NATIVA de las tiendas. Es el mismo `dist/` adentro de Capacitor,
 *    así que este código corre ahí también. Bloquearla sería publicar en las
 *    tiendas una app que al abrirse dice «instalá la app»;
 *  · la COMPUTADORA. Nadie agrega nada a la pantalla de inicio de una laptop,
 *    y quien administra un club arma los torneos ahí;
 *  · ya instalada, que es de lo que se trata.
 */

/** Qué teléfono es, o ninguno. */
export type Plataforma = 'ANDROID' | 'IOS' | 'OTRA';

export interface Entorno {
  userAgent: string;
  /** `navigator.standalone`, que solo existe en Safari de iOS. */
  iosStandalone: boolean | undefined;
  /** `matchMedia('(display-mode: standalone)').matches`. */
  displayStandalone: boolean;
  /** `window.Capacitor !== undefined`: estamos adentro de la app nativa. */
  enCapacitor: boolean;
  /** iPadOS 13+ miente y dice ser un Mac; esto es lo que lo delata. */
  maxTouchPoints: number;
  platform: string;
  /**
   * YA PIDIÓ SEGUIR SIN INSTALAR, y en este teléfono se le respeta.
   *
   * Lo pone `/mobile`. Ver `RUTA_SIN_PUERTA` y `quéPedir`.
   */
  dejóPasar: boolean;
  /**
   * EL ENLACE VINO DE UN CORREO NUESTRO, así que pasa derecho.
   *
   * A diferencia de `dejóPasar`, esto NO SE RECUERDA: vale para esta visita y
   * nada más. Ver `MARCA_DE_CORREO`.
   */
  desdeCorreo: boolean;
}

/**
 * EL IPAD MIENTE. Desde iPadOS 13 el Safari del iPad manda un `userAgent` de
 * Mac —«Macintosh; Intel Mac OS X»— para que las webs le den la versión de
 * escritorio. La única forma de distinguirlo de una laptop de verdad es que
 * la laptop no tiene pantalla táctil: `maxTouchPoints` es 0 en un Mac y 5 en
 * un iPad.
 */
function esIPadDisfrazado(entorno: Entorno): boolean {
  return entorno.platform === 'MacIntel' && entorno.maxTouchPoints > 1;
}

export function plataformaDe(entorno: Entorno): Plataforma {
  const ua = entorno.userAgent;
  // ANDROID ANTES QUE IOS, y no al revés: el Chrome de Android dice
  // «Android» y también «Safari» en el mismo texto, así que buscar iOS
  // primero por la palabra «Safari» clasificaría medio Android como iPhone.
  if (/android/i.test(ua)) return 'ANDROID';
  if (/iphone|ipad|ipod/i.test(ua) || esIPadDisfrazado(entorno)) return 'IOS';
  return 'OTRA';
}

/** Ya está instalada: abierta desde el ícono, o adentro de la app nativa. */
export function yaInstalada(entorno: Entorno): boolean {
  return entorno.enCapacitor || entorno.displayStandalone || entorno.iosStandalone === true;
}

/**
 * NAVEGADORES QUE VIVEN ADENTRO DE OTRA APP: WhatsApp, Instagram, Facebook.
 *
 * POR QUÉ MERECEN SU PROPIA RESPUESTA. Desde ahí NO SE PUEDE instalar nada:
 * no hay menú de Safari, no hay «agregar a inicio», y en iOS tampoco hay
 * evento de instalación. Y es exactamente por donde llega la gente, porque
 * esta aplicación se comparte por WhatsApp.
 *
 * Un bloqueo que dijera «instalá la app» ahí sería una puerta cerrada sin
 * llave: la persona no tiene NINGÚN camino, por más que quiera. Lo que sí se
 * puede es mandarla a abrir el enlace en el navegador de verdad, y eso es lo
 * que la pantalla le va a decir.
 */
export function enNavegadorDeOtraApp(userAgent: string): boolean {
  return /FBAN|FBAV|FB_IAB|Instagram|Line\/|MicroMessenger|WhatsApp/i.test(userAgent);
}

/**
 * LA DIRECCIÓN QUE SALTEA LA PUERTA: `tennisup.club/mobile`.
 *
 * QUÉ PROBLEMA RESUELVE. La puerta funciona, pero hay gente que no sabe
 * instalar una app desde el navegador —o no puede, con el teléfono lleno— y
 * se queda afuera sin entender por qué. Javier: «si entran directo a
 * tennisup.club mostrar eso, si entran a tennisup.club/mobile mostrar el login».
 *
 * ES UNA SALIDA, NO UNA PUERTA ABIERTA. Quien llega a la raíz sigue viendo el
 * instructivo igual que hasta hoy; esto es la dirección que se le pasa por
 * WhatsApp a quien ya se trabó. Por eso no hay ningún enlace hacia acá desde
 * la pantalla de instalar: si lo hubiera, lo tocaría todo el mundo y nadie
 * instalaría nada.
 */
export const RUTA_SIN_PUERTA = '/mobile';

/**
 * LA MARCA QUE LLEVAN LOS ENLACES DE LOS CORREOS.
 *
 * QUÉ PASÓ. Javier tocó «Ver el reto» en el correo y en vez del reto le salió
 * el instructivo de instalar. La puerta se traga CUALQUIER enlace que venga de
 * afuera, y ahí no hay nada que devuelva al destino: el enlace se pierde.
 *
 * Es el peor lugar donde ponerle una puerta a alguien. Quien toca un botón de
 * un correo no está explorando la aplicación: viene a ver UNA cosa, y le
 * llegó un aviso justamente para que la viera.
 *
 * POR QUÉ UNA MARCA EN LA DIRECCIÓN Y NO «detectar si la app está instalada».
 * Javier pidió eso y NO SE PUEDE: ni Safari ni Chrome le dicen a una página si
 * hay una app instalada en el teléfono, a propósito —sería una forma de
 * rastrear qué tiene instalado cada quien—. Lo único que la página sabe es si
 * ELLA MISMA está corriendo instalada, y abriendo desde el correo nunca lo
 * está: está en el navegador. Así que la pregunta se cambia por una que sí
 * tiene respuesta: «¿este enlace lo mandamos nosotros?».
 *
 * NO ABRE LA PUERTA PARA SIEMPRE, al revés que `/mobile`: no se anota nada.
 * Quien entra por un correo ve lo que vino a ver, y si después vuelve por la
 * puerta principal se le pide instalar igual que a todo el mundo.
 */
export const MARCA_DE_CORREO = 'e';

/**
 * ¿ESTE ENLACE VINO DE UN CORREO NUESTRO?
 *
 * Se mira la dirección y nada más. NO es una comprobación de seguridad —
 * cualquiera puede escribir `?e=1` a mano— y no hace falta que lo sea: lo peor
 * que consigue quien lo haga es saltarse un instructivo de instalación, que es
 * exactamente lo que `/mobile` ya ofrece a quien lo pida.
 */
export function vieneDeUnCorreo(search: string): boolean {
  try {
    return new URLSearchParams(search).get(MARCA_DE_CORREO) === '1';
  } catch {
    // Una dirección que no se puede leer no es una dirección de correo.
    return false;
  }
}

/**
 * SE MIRA AL CARGAR EL MÓDULO, ANTES DE QUE REACT MONTE NADA.
 *
 * ACÁ ESTABA EL DEFECTO, Y NO EN LA REGLA. La marca se leía en un `useEffect`,
 * que corre DESPUÉS del primer dibujo — y para entonces el router ya navegó:
 * quien no tiene sesión abierta en ese navegador cae en `/login`, y esa
 * redirección se lleva la query puesta.
 *
 * La pantalla lo dijo con todas las letras cuando Javier mandó la captura:
 * «dbg: IOS · search=(vacío) · path=/login». El enlace traía `?v=mine&e=1` y
 * la puerta leyó una dirección vacía.
 *
 * ESTE MÓDULO SE EVALÚA AL IMPORTARSE, antes de que exista el router, así que
 * `window.location.search` es todavía la dirección con la que se abrió la
 * pestaña. Es el único momento en que se puede confiar en ella.
 *
 * NO SE PUEDE PROBAR CON `entornoDelNavegador()` sola —esa constante se
 * congela al importar el módulo, y en las pruebas eso pasa una vez para todo
 * el archivo— así que `vieneDeUnCorreo` sigue siendo pública y se prueba
 * aparte: es la regla, y esto es el momento de mirarla.
 */
const LLEGÓ_DESDE_UN_CORREO =
  typeof window !== 'undefined' && vieneDeUnCorreo(window.location.search);

/**
 * DÓNDE SE RECUERDA QUE ESTA PERSONA YA PIDIÓ PASAR.
 *
 * Sin memoria, `/mobile` no sirve para nada: mostraría el login y al primer
 * toque en «Clubes» la puerta volvería a aparecer. Se guarda en el teléfono y
 * no en la cuenta a propósito — el problema es del aparato, no de la persona:
 * la misma cuenta en un teléfono donde SÍ instaló la app debería seguir
 * usándola instalada.
 */
export const LLAVE_SIN_PUERTA = 'tennisup.sinPuerta';

/** Qué pantalla corresponde. `null` = seguí de largo, no hay nada que pedir. */
export type QuéPedir =
  /** Android con el evento del navegador: hay un botón que instala de verdad. */
  | 'ANDROID'
  /** iOS en Safari: no hay botón, hay instructivo — Compartir → Agregar a inicio. */
  | 'IOS'
  /** Adentro de WhatsApp o Instagram: primero hay que salir al navegador. */
  | 'ABRIR_EN_NAVEGADOR'
  | null;

/**
 * LA DECISIÓN, entera y en un solo lugar.
 *
 * El orden importa y no es arbitrario: primero todo lo que significa «acá no
 * hay nada que pedir» —la app nativa, la computadora, la ya instalada— y
 * recién después qué pedirle a un teléfono. Al revés, cualquier error de
 * clasificación de teléfono se convierte en una laptop bloqueada.
 */
export function quéPedir(entorno: Entorno): QuéPedir {
  if (yaInstalada(entorno)) return null;
  // YA PIDIÓ PASAR, en este teléfono. Va arriba de todo lo demás: es una
  // decisión de la persona y no una deducción nuestra sobre su aparato.
  if (entorno.dejóPasar) return null;
  /*
   * VIENE DE UN CORREO NUESTRO: pasa derecho.
   *
   * Le mandamos un aviso para que viera algo y le pusimos una puerta delante,
   * sin nada que lo devolviera al destino. Ver `MARCA_DE_CORREO`.
   *
   * VA JUNTO A LOS OTROS DOS «no hay nada que pedir» y antes de mirar la
   * plataforma, por la misma razón que dice el comentario de arriba: primero
   * todo lo que significa dejar pasar, y recién después qué pedirle a un
   * teléfono.
   */
  if (entorno.desdeCorreo) return null;

  const plataforma = plataformaDe(entorno);
  if (plataforma === 'OTRA') return null;

  if (enNavegadorDeOtraApp(entorno.userAgent)) return 'ABRIR_EN_NAVEGADOR';
  return plataforma === 'ANDROID' ? 'ANDROID' : 'IOS';
}

/** Lo que el navegador sabe de sí mismo, leído en un solo lugar. */
export function entornoDelNavegador(): Entorno {
  const nav = navigator as Navigator & { standalone?: boolean };
  return {
    userAgent: nav.userAgent,
    iosStandalone: nav.standalone,
    displayStandalone:
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(display-mode: standalone)').matches,
    enCapacitor: (window as { Capacitor?: unknown }).Capacitor !== undefined,
    maxTouchPoints: nav.maxTouchPoints,
    platform: nav.platform,
    dejóPasar: yaPidióPasar(),
    /*
     * LO QUE SE MIRÓ AL ABRIR LA PESTAÑA, no lo que hay ahora: cuando esto
     * corre, el router ya pudo haber mandado a `/login` y borrado la query.
     */
    desdeCorreo: LLEGÓ_DESDE_UN_CORREO,
  };
}

/**
 * ¿Ya pidió pasar en este teléfono?
 *
 * Lee `localStorage` adentro de un `try`: en navegación privada de iOS
 * tocarlo LANZA, y una excepción acá dejaría la pantalla en blanco en vez de
 * mostrar la puerta. Si no se puede leer, la respuesta es «no pidió», que es
 * el lado seguro: se ve el instructivo, que es lo que pasaba hasta hoy.
 */
export function yaPidióPasar(): boolean {
  try {
    return window.localStorage.getItem(LLAVE_SIN_PUERTA) === '1';
  } catch {
    return false;
  }
}

/** Lo deja anotado. Falla en silencio por lo mismo que `yaPidióPasar`. */
export function recordarQuePasó(): void {
  try {
    window.localStorage.setItem(LLAVE_SIN_PUERTA, '1');
  } catch {
    // Sin memoria, la puerta vuelve en la próxima pantalla. Es peor que
    // funcione a medias, pero mucho mejor que una pantalla en blanco.
  }
}
